import React, { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import Pagination from 'rsuite/Pagination';
import MyLoader from '../../common/loader.component';
import { OutletContextParams } from './delivery-orders-screen';
import { usePaginationQs } from '../../../utils/usePagination';
import { useSearchParamsPaginationEffect } from '../purchase-order/useSearchParamsPaginationEffect';
import { PackagingTabItem } from './packaging-tab-item.component';
import { ExpandButton } from '../../row';
import { checkDeliveryReadyToSent } from '../../../utils/common.utils';
import { useDeliveryOrders } from '../../../api/use-delivery-orders';
import {
  CleanDeliveryOrder,
  CleanSupplierWarehouse,
} from '../../../export-types/cleaned-types';
import { ModalsService } from '../../../services/modals.service';
import { DeliveryChangeStorageLocationName } from './delivery-change-storage-location-name-modal';
import { useIntl } from 'react-intl';
import moment from 'moment';

export const PackagingTab = () => {
  const { take, skip, handleSetPage, handleChangeLimit, page } =
    usePaginationQs(15);
  const intl = useIntl();

  useSearchParamsPaginationEffect(handleSetPage);

  const [collapsedStorage, setCollapsedStorage] = useState<string[]>([]);

  const {
    debouncedInternalId,
    linked,
    expanded,
    collapsed,
    onChangeCollapsed,
    selectSupplier,
    direction,
    selectAllOrders,
    onChangeSelectedOrders,
  } = useOutletContext<OutletContextParams>();

  const { data, error, isLoading, mutate } = useDeliveryOrders({
    linked,
    status: ['packaged'],
    take,
    skip,
    internalId: debouncedInternalId || undefined,
    supplierId: selectSupplier || undefined,
    direction,
  });

  const onToggle = (storage: string) => {
    if (collapsed.includes(storage)) {
      const newCollapsed = [...collapsed];
      newCollapsed.splice(newCollapsed.indexOf(storage), 1);
      onChangeCollapsed(newCollapsed);
    } else {
      onChangeCollapsed([...collapsed, storage]);
    }
  };

  const onToggleStorage = (storage: string) => {
    if (collapsedStorage.includes(storage)) {
      const newCollapsed = [...collapsedStorage];
      newCollapsed.splice(newCollapsed.indexOf(storage), 1);
      setCollapsedStorage(newCollapsed);
    } else {
      setCollapsedStorage([...collapsedStorage, storage]);
    }
  };

  const ordersByStorages = useMemo(() => {
    if (!data?.items) return {};

    return data.items.reduce<
      Record<
        string,
        {
          orders: CleanDeliveryOrder[];
          count: number;
          weight: number;
          volume: number;
          supplierWarehouse?: CleanSupplierWarehouse | null;
          packagedDate: Date;
        }
      >
    >((acc, item) => {
      const storageLocation = item.storageLocation;
      if (!storageLocation) return acc;
      if (!acc[storageLocation]) {
        acc[storageLocation] = {
          orders: [],
          count: 0,
          weight: 0,
          volume: 0,
          packagedDate: new Date(),
          supplierWarehouse: item.purchaseOrders?.length
            ? item.purchaseOrders[0].supplierWarehouse
            : undefined,
        };
      }
      acc[storageLocation].orders.push(item);
      acc[storageLocation].count += item?.count || 0;
      acc[storageLocation].weight += +(item?.weight || 0);
      acc[storageLocation].volume += +(item?.volume || 0);
      const packagedDate = moment(item.packagedDate);
      acc[storageLocation].packagedDate = packagedDate.isBefore(
        acc[storageLocation].packagedDate,
      )
        ? packagedDate.toDate()
        : acc[storageLocation].packagedDate;

      return acc;
    }, {});
  }, [data]);

  useEffect(() => {
    if (selectAllOrders) {
      onChangeSelectedOrders(
        data?.items.filter(checkDeliveryReadyToSent) || [],
      );
    } else {
      onChangeSelectedOrders([]);
    }
  }, [selectAllOrders]);

  if (isLoading) {
    return (
      <>
        <div className="pb-4">
          <MyLoader />
        </div>
      </>
    );
  }

  if (error || !data) {
    return (
      <>
        <div className="pb-4">Неизвестная ошибка</div>
      </>
    );
  }

  return (
    <>
      <div className="pb-4 ">
        {Object.entries(ordersByStorages)
          .sort(
            ([, a], [, b]) =>
              b.packagedDate.getTime() - a.packagedDate.getTime(),
          )
          .map(([key, items]) => {
            const supplierWarehouse = items.supplierWarehouse;
            return (
              <div className={'border-bottom mb-3'}>
                <div className={'d-flex'}>
                  <span className={'fs-21  mx-2'}>
                    {' '}
                    {supplierWarehouse
                      ? supplierWarehouse.location?.name
                      : intl.formatMessage({
                          id: 'deliveryOrder.location',
                          defaultMessage: 'Место',
                          description: 'Надпись на вкладке',
                        })}
                    : {key}
                  </span>
                  <div className={'align-items-center mt-1'}>
                    <i
                      className={
                        'bi bi-pencil-square hover-bg-light rounded-5 cursor-pointer '
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        ModalsService.createModal(
                          DeliveryChangeStorageLocationName,
                          {
                            oldName: key,
                          },
                        ).then(() => {
                          mutate();
                        });
                      }}
                    ></i>
                  </div>
                  <div className={'mleft-auto d-flex gap-1 '}>
                    <span className={'mt-1 fs-18'}>
                      {intl.formatMessage({
                        id: 'deliveryOrder.item',
                        defaultMessage: 'Мест',
                        description: 'Надпись на вкладке',
                      })}
                      {ordersByStorages[key].count};{' '}
                      {ordersByStorages[key].volume.toFixed(2)}
                      {intl.formatMessage({
                        id: 'deliveryOrder.volume',
                        defaultMessage: 'м³',
                        description: 'Надпись на вкладке',
                      })}
                      ;{ordersByStorages[key].weight}{' '}
                      {intl.formatMessage({
                        id: 'deliveryOrder.kg',
                        defaultMessage: 'кг',
                        description: 'Надпись на вкладке',
                      })}
                    </span>
                    <div className={''}>
                      <ExpandButton
                        onToggle={() => onToggleStorage(key)}
                        collapsed={collapsedStorage.includes(key)}
                      />
                    </div>
                  </div>
                </div>
                {!collapsedStorage.includes(key) &&
                  ordersByStorages[key].orders.map((data) => (
                    <PackagingTabItem
                      key={data.id}
                      data={data}
                      onCollapsedChange={() => {
                        onToggle(data.id);
                      }}
                      collapsed={
                        (!expanded && collapsed.includes(data.id)) ||
                        (expanded && !collapsed.includes(data.id))
                      }
                      onUpdate={() => {
                        mutate();
                      }}
                    />
                  ))}
              </div>
            );
          })}
      </div>
      {data.total > take && (
        <div className="pb-4">
          <Pagination
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            maxButtons={5}
            size="md"
            layout={['pager']}
            total={data?.total}
            limitOptions={[15, 20]}
            limit={take}
            activePage={page}
            onChangePage={handleSetPage}
            onChangeLimit={handleChangeLimit}
          />
        </div>
      )}
    </>
  );
};
